// src/pages/Profile.jsx
import React, { useState, useEffect } from 'react';
import { AiOutlineEye, AiOutlineEyeInvisible } from 'react-icons/ai';
import '../styles/Profile.css';
import toast, { Toaster } from 'react-hot-toast';
import { API_BASE, parseJsonResponse, getUserErrorMessage } from '../utils/apiBase';
import { authFetch } from '../utils/apiAuth';

export default function Profile() {
  const [basicInfo, setBasicInfo] = useState({
    name: '',
    email: '',
    phone: '',
  });
  const [passwordData, setPasswordData] = useState({
    newPassword: '',
    confirmPassword: '',
  });
  const [showPasswords, setShowPasswords] = useState({
    new: false,
    confirm: false,
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [updatingPassword, setUpdatingPassword] = useState(false);

  const handleBasicInfoChange = (e) => {
    const { name, value } = e.target;
    setBasicInfo((prev) => ({ ...prev, [name]: value }));
  };

  const handlePasswordChange = (e) => {
    const { name, value } = e.target;
    setPasswordData((prev) => ({ ...prev, [name]: value }));
  };

  const togglePasswordVisibility = (field) => {
    setShowPasswords((prev) => ({ ...prev, [field]: !prev[field] }));
  };

  const handleSaveBasicInfo = async () => {
    const user = JSON.parse(localStorage.getItem('userType'));
    const userId = user?.id;

    if (!basicInfo.name.trim()) {
      toast.error('Name is required');
      return;
    }

    if (!basicInfo.phone.trim()) {
      toast.error('Phone number is required');
      return;
    }

    if (!userId) {
      toast.error('User ID missing');
      return;
    }

    setSaving(true);
    try {
      const formData = new FormData();
      formData.append('name', basicInfo.name);
      formData.append('phone', basicInfo.phone);

      const response = await fetch(`${API_BASE}/v1/user/${userId}`, {
        method: 'PUT',
        body: formData,
      });

      const result = await response.json();

      if (response.ok && result.success) {
        toast.success('Profile updated successfully');
      } else {
        toast.error(result.message || 'Failed to update profile');
      }
    } catch (error) {
      console.error(error);
      toast.error('Something went wrong while updating');
    } finally {
      setSaving(false);
    }
  };

  const handleUpdatePassword = async () => {
    if (!passwordData.newPassword.trim()) {
      toast.error('New password is required');
      return;
    }

    if (passwordData.newPassword !== passwordData.confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    if (passwordData.newPassword.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }

    setUpdatingPassword(true);
    try {
      const response = await authFetch(`${API_BASE}/v1/user/change-password`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ newPassword: passwordData.newPassword }),
      });

      await parseJsonResponse(response);
      toast.success('Password updated successfully');
      setPasswordData({ newPassword: '', confirmPassword: '' });
    } catch (error) {
      toast.error(getUserErrorMessage(error, 'Failed to update password'));
    } finally {
      setUpdatingPassword(false);
    }
  };

  useEffect(() => {
    const fetchUserData = async () => {
      const user = JSON.parse(localStorage.getItem('userType'));
      const userId = user?.id;

      if (!userId) {
        toast.error('User ID not found');
        setLoading(false);
        return;
      }

      setLoading(true);
      try {
        const response = await fetch(`${API_BASE}/v1/user/${userId}`);
        const result = await response.json();

        if (response.ok && result.success) {
          setBasicInfo({
            name: result.user.name || '',
            email: result.user.email || '',
            phone: result.user.phone || '',
          });
        } else {
          toast.error(result.message || 'Failed to load profile');
        }
      } catch (error) {
        console.error('Fetch error:', error);
        toast.error('Something went wrong while loading profile');
      } finally {
        setLoading(false);
      }
    };

    fetchUserData();
  }, []);

  const passwordFields = [
    { id: 'new', name: 'newPassword', label: 'New Password', key: 'new' },
    { id: 'confirm', name: 'confirmPassword', label: 'Confirm Password', key: 'confirm' },
  ];

  return (
    <div className="profile_main">
      <Toaster position="top-center" />
      <div className="profile-container">
        <h1 className="profile-title">My Profile</h1>

        <div className="forms-section">
          {loading ? (
            <div className="profile-loading">
              <span className="profile-loader" />
            </div>
          ) : (
            <>
              <div className="form-box basic-info">
                <h2>Basic Information</h2>

                <label htmlFor="profile-name">Name</label>
                <input
                  id="profile-name"
                  type="text"
                  name="name"
                  value={basicInfo.name}
                  onChange={handleBasicInfoChange}
                />

                <label htmlFor="profile-email">Email</label>
                <input
                  id="profile-email"
                  type="email"
                  name="email"
                  value={basicInfo.email}
                  disabled
                />

                <label htmlFor="profile-phone">Phone</label>
                <input
                  id="profile-phone"
                  type="text"
                  name="phone"
                  value={basicInfo.phone}
                  placeholder="Phone"
                  onChange={handleBasicInfoChange}
                />

                <button type="button" className="form-submit-btn" onClick={handleSaveBasicInfo} disabled={saving}>
                  {saving ? 'Saving...' : 'Save'}
                </button>
              </div>

              <div className="form-box security">
                <h2>Change Password</h2>

                {passwordFields.map(({ id, name, label, key }) => (
                  <div key={id} className="profile-password-field">
                    <label htmlFor={`profile-${id}`}>{label}</label>
                    <div className="profile-password-input-wrap">
                      <input
                        id={`profile-${id}`}
                        type={showPasswords[key] ? 'text' : 'password'}
                        name={name}
                        value={passwordData[name]}
                        onChange={handlePasswordChange}
                        autoComplete="new-password"
                      />
                      <span
                        role="button"
                        tabIndex={0}
                        className="profile-password-toggle"
                        onClick={() => togglePasswordVisibility(key)}
                        onKeyDown={(e) => e.key === 'Enter' && togglePasswordVisibility(key)}
                        aria-label={showPasswords[key] ? 'Hide password' : 'Show password'}
                      >
                        {showPasswords[key] ? <AiOutlineEyeInvisible /> : <AiOutlineEye />}
                      </span>
                    </div>
                  </div>
                ))}

                <button type="button" className="form-submit-btn" onClick={handleUpdatePassword} disabled={updatingPassword}>
                  {updatingPassword ? 'Updating...' : 'Update Password'}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
